import type React from "react";
import { ArrowRight, Home, Layers } from "lucide-react";
import useSWR from "swr";

import { useCourtDashboardStats } from "./useCourtDashboardStats";
import type { HighCourtRow } from "./highcourt/types";

interface CourtsHomeProps {
  onHighCourts: () => void;
  onDistrictCourts: () => void;
}

export const CourtsHome = ({
  onHighCourts,
  onDistrictCourts,
}: CourtsHomeProps) => {
  const { data: highCourtResponse, isLoading: highCourtsLoading } = useSWR(
    "/api/court/get-highcourt",
  );
  const highCourts = (highCourtResponse?.data || []) as HighCourtRow[];
  const { stats, isLoading: dashboardStatsLoading } =
    useCourtDashboardStats(highCourts);
  const isStatsLoading = highCourtsLoading || dashboardStatsLoading;

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-xl font-bold">Courts</h1>
        <p className="mt-1 text-sm text-slate-500">
          Select the type of court you want to manage
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <CourtTypeCard
          icon={<Home className="h-6 w-6 text-blue-600" />}
          tint="blue"
          title="High Courts"
          description="Manage high courts, their benches, and court halls. Add judges, set locations, and configure status."
          // meta={`${formatCount(stats.highCourtCount, isStatsLoading)} high courts`}
          secondaryMeta={`${formatCount(stats.benchCount, isStatsLoading)} Benches`}
          tertiaryMeta={`${formatCount(stats.courtHallCount, isStatsLoading)} Court Halls`}
          buttonLabel="Manage High Courts"
          onClick={onHighCourts}
        />
        <CourtTypeCard
          icon={<Layers className="h-6 w-6 text-[#f26422]" />}
          tint="teal"
          title="District Courts"
          description="Manage district courts, complexes, establishments, and court rooms across all districts in the state."
          meta={`${formatCount(stats.districtCount, isStatsLoading)} Complexes`}
          secondaryMeta={`${formatCount(stats.courtRoomCount, isStatsLoading)} Court Rooms`}
          buttonLabel="Manage District Courts"
          onClick={onDistrictCourts}
        />
      </div>

      <div className="mt-10 rounded-xl border border-slate-200 bg-white px-4 py-6 sm:px-8">
        <div className="grid grid-cols-1 gap-y-6 text-center sm:grid-cols-2">
          <DashboardStat
            color="text-blue-700"
            value={"25"}
            label="High Courts"
          />

          <DashboardStat
            color="text-[#f26422]"
            value={"743"}
            label="District Courts"
          />
        </div>
      </div>
    </div>
  );
};

interface DashboardStatProps {
  color: string;
  value: string;
  label: string;
}

const DashboardStat = ({ color, value, label }: DashboardStatProps) => (
  <div className="border-slate-200 sm:border-r last:sm:border-r-0">
    <div className={`text-3xl font-bold ${color}`}>{value}</div>
    <div className="text-sm text-slate-500">{label}</div>
  </div>
);

interface CourtTypeCardProps {
  icon: React.ReactNode;
  tint: "blue" | "teal";
  title: string;
  description: string;
  meta?: string;
  secondaryMeta: string;
  tertiaryMeta?: string;
  buttonLabel: string;
  onClick: () => void;
}

const CourtTypeCard = ({
  icon,
  tint,
  title,
  description,
  meta,
  secondaryMeta,
  tertiaryMeta,
  buttonLabel,
  onClick,
}: CourtTypeCardProps) => {
  const colors =
    tint === "blue"
      ? "border-blue-200 hover:border-blue-500 bg-blue-600"
      : "border-orange-200 hover:border-orange-500 bg-orange";

  return (
    <section
      className={`rounded-2xl border-2 bg-white p-6 transition ${colors
        .split(" ")
        .slice(0, 2)
        .join(" ")}`}
    >
      <div
        className={`mb-3 flex h-12 w-12 items-center justify-center rounded-2xl ${
          tint === "blue" ? "bg-blue-50" : "bg-[#fde8d8] "
        }`}
      >
        {icon}
      </div>
      <h2 className="text-md font-bold">{title}</h2>
      <p className="mt-3 max-w-3xl text-sm leading-6 text-slate-500">
        {description}
      </p>
      <div className="mt-7 flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-500">
        <span>{meta}</span>
        <span>{secondaryMeta}</span>
        {tertiaryMeta && <span>{tertiaryMeta}</span>}
      </div>
      <button
        type="button"
        onClick={onClick}
        className={`mt-5 inline-flex w-full items-center justify-center gap-3 rounded-lg px-6 py-2 font-semibold text-white sm:w-auto ${
          tint === "blue"
            ? "bg-blue-600 hover:bg-blue-700"
            : "bg-[#f26422] hover:bg-[#f26422]-700"
        }`}
      >
        {buttonLabel}
        <ArrowRight className="h-4 w-4" />
      </button>
    </section>
  );
};

const formatCount = (value: number, isLoading: boolean) =>
  isLoading ? "..." : String(value);
